import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { CommonModule } from '@angular/common';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { ApiService } from 'src/app/core/services/api.service';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatChipsModule } from '@angular/material/chips';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatIconModule } from '@angular/material/icon';

interface User {
  email: string;
}

interface UserResponse {
  start: number;
  limit: number;
  length: number;
  users: User[];
  total: number;
}

@Component({
  selector: 'app-modify-diligence',
  standalone: true,
  imports: [
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatCardModule,
    CommonModule,
    MatAutocompleteModule,
    MatChipsModule,
    MatTooltipModule,
    MatIconModule
  ],
  template: `
    <mat-card class="mx-auto p-6">
      <mat-card-header>
        <mat-card-title>Admin Settings</mat-card-title>
        <div class="ml-auto flex items-center space-x-2">
          <button
            *ngIf="!isReportVoided"
            mat-stroked-button
            color="warn"
            class="flex items-center"
            matTooltip="Void this report and return the credit to the customer's account"
            (click)="toggleReportVoidStatus()"
          >
            <mat-icon>cancel</mat-icon>
            <span class="ml-1">Void</span>
          </button>

          <div *ngIf="isReportVoided" class="flex items-center space-x-2 border border-red-200 bg-red-50 rounded-md p-2">
            <mat-chip color="warn" selected matTooltip="This report has been voided and the credit has been returned to the customer's account">
              VOIDED
            </mat-chip>
            <button
              mat-stroked-button
              color="primary"
              class="flex items-center h-8 py-0"
              matTooltip="Unvoid this report and subtract the credit from the customer's account"
              (click)="toggleReportVoidStatus()"
            >
              <mat-icon class="text-sm">restore</mat-icon>
              <span class="ml-1">Undo</span>
            </button>
          </div>
        </div>
      </mat-card-header>
      <div class="h-4"></div>
      <mat-card-content>
        <form (ngSubmit)="onUpdateDiligence()" class="flex flex-col space-y-4">
          <mat-form-field appearance="outline">
            <mat-label>User Email</mat-label>
            <input matInput
                   type="text"
                   [(ngModel)]="diligence.userEmail"
                   name="userEmail"
                   [matAutocomplete]="auto"
                   (input)="searchUsers($event)">
            <mat-autocomplete #auto="matAutocomplete">
              <mat-option *ngFor="let user of filteredUsers" [value]="user.email">
                {{ user.email }}
              </mat-option>
            </mat-autocomplete>
          </mat-form-field>

          <mat-form-field appearance="outline">
            <mat-label>Phase</mat-label>
            <mat-select [(ngModel)]="diligence.phase" name="phase">
              <mat-option *ngFor="let i of [1, 2, 3, 4, 5, 6]" [value]="i">
                {{ i }}
              </mat-option>
            </mat-select>
          </mat-form-field>

          <div class="flex justify-end">
            <button mat-raised-button color="primary" type="submit">
              Save Changes
            </button>
          </div>
        </form>
      </mat-card-content>
    </mat-card>
  `,
  styles: [`
    :host {
      display: block;
      padding: 20px;
    }
  `]
})
export class ModifyDiligenceComponent implements OnInit {
  @Input() diligence: any;
  @Output() updateDiligence = new EventEmitter<any>();
  @Output() voidStatusChanged = new EventEmitter<boolean>();

  private searchTerms = new Subject<string>();
  allUsers: User[] = [];
  filteredUsers: User[] = [];
  isReportVoided = false;

  constructor(private apiService: ApiService, private snackBar: MatSnackBar) {
    this.searchTerms.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(term => {
      this.filterUsers(term);
    });
  }

  ngOnInit() {
    this.apiService.getUsers().subscribe((response: UserResponse) => {
      this.allUsers = response.users;
    });

    this.checkReportVoidStatus();
  }

  searchUsers(event: any) {
    this.searchTerms.next(event.target.value);
  }

  private filterUsers(term: string) {
    const searchTerm = term.toLowerCase();
    this.filteredUsers = this.allUsers.filter(user =>
      user.email.toLowerCase().includes(searchTerm)
    );
  }

  checkReportVoidStatus() {
    if (this.diligence?.credit?.status === 'voided') {
      this.isReportVoided = true;
    } else {
      this.isReportVoided = false;
    }
  }

  toggleReportVoidStatus() {
    const action = this.isReportVoided ? 'unvoid' : 'void';
    const reportId = this.diligence.uuid;

    this.apiService.updateReportStatus(reportId, action).subscribe({
      next: () => {
        this.isReportVoided = !this.isReportVoided;

        this.snackBar.open(
          `Report ${this.isReportVoided ? 'voided' : 'unvoided'} successfully. ${this.isReportVoided ? 'Credit has been returned to the customer\'s account.' : ''}`,
          'Close',
          { duration: 5000 }
        );

        this.voidStatusChanged.emit(this.isReportVoided);
      },
      error: (error) => {
        this.snackBar.open(
          `Failed to ${action} report: ${error.error?.error || 'Unknown error'}`,
          'Close',
          { duration: 5000 }
        );
      }
    });
  }

  onUpdateDiligence() {
    this.updateDiligence.emit(this.diligence);
  }
}
